import { useState } from "react";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// React Query
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

// Hooks/Contexts
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// API
import { getPto, getPtoBalance, requestPtoBalanceIngestion, requestPtoIngestion } from "@DataPages/api/timeAndPayRates";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import PtoContent from "./PtoContent";
import { and, or } from "@/Common/helpers/sonar";

// MUI
import { Grid, Stack } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IDepartmentPosition, IPayPeriodPto, IPtoDetails, IUserPto } from "@/DataPages/types/timeAndPayRates/models";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { Nullable } from "@/Common/interfaces/types";

// Constants
import actionsList from "@Constants/actionsList";
import { getPositionDepartment } from "@/Common/helpers/Positions";
import { DATE_FORMAT } from "@/Constants/date";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

const getLastUpdatedDate = (date: string, timezone: string | undefined, dateFormat = DATE_FORMAT.usDateTime24hSeconds) => {
	if (!date) {
		return null;
	}
	return getFormattedDate(date, timezone ?? "", dateFormat) ?? "";
};

const getEmptyTitle = (selected: IPeriodTreeSelection, isAccessGranted: boolean) => {
	if (!isAccessGranted) {
		return "You do not have access to view PTO";
	}
	if (!selected) {
		return "Select a pay period to view their PTO";
	}
	return "No PTO found for this pay period";
};

const matchDepartments = (positions: Nullable<string[]> = [], ptoBalances: Nullable<IUserPto[]> = [], ptos: Nullable<IUserPto[]> = []) => {
	const departments = new Map();
	const filteredPtoBalances: IUserPto[] = [];
	const filteredPtos: IUserPto[] = [];

	if (!positions?.length || !ptoBalances?.length || !ptos?.length) {
		return [];
	}
	ptoBalances.forEach((record) => {
		if (record?.user && positions.includes(record.user.position)) {
			filteredPtoBalances.push(record);
		}
	});

	ptos.forEach((record) => {
		if (record?.user && positions.includes(record.user.position)) {
			filteredPtos.push(record);
		}
	});

	if (filteredPtoBalances.length || filteredPtos.length) {
		const information = mergeRecords(filteredPtoBalances, filteredPtos);

		positions.forEach((position) => {
			const department = getPositionDepartment(position);
			const stored = departments.get(department) || { department, listPositions: [] };
			const records = getPositionRecords(position, information);
			if (records?.length) {
				stored.listPositions.push({
					position,
					records,
				});

				departments.set(department, stored);
			}
		});
	}

	return Array.from(departments.values());
};

const mergeRecords = (usersPtoBalances: IUserPto[] = [], usersPto: IUserPto[] = []) => {
	const recordsMap = new Map();

	usersPtoBalances.forEach((element) => {
		const record = recordsMap.get(element.user._id) || {
			_id: element.user._id,
			fullName: element.user.fullName,
			position: element.user.position,
			balance: 0,
			requested: 0,
			details: [],
		};

		const details: IPtoDetails[] = [];
		const ptoBalances = usersPtoBalances.filter((balance) => balance.user._id === element.user._id);
		const userPtos = usersPto.filter((pto) => pto.user._id === element.user._id);
		ptoBalances.forEach((ptoBalance) => {
			let buyout = 0;
			if (ptoBalance.payType === "Vacation") {
				record.balance = ptoBalance.hours;
				buyout = userPtos.find((entry) => entry.payType === "Buyout")?.hours ?? 0;
			}
			const pto = userPtos.find((entry) => entry.payTypeID === ptoBalance.payTypeID);
			details.push({
				typeId: ptoBalance.payTypeID,
				type: ptoBalance.payType,
				balance: ptoBalance.hours,
				requested: (pto?.hours ?? 0) + buyout,
				approvedDates: pto?.details?.map((date) => date.start) ?? [],
			});
		});
		record.details = details;
		recordsMap.set(element.user._id, record);
	});

	usersPto.forEach((element) => {
		const record = recordsMap.get(element.user._id) || {
			_id: element.user._id,
			fullName: element.user.fullName,
			position: element.user.position,
			balance: 0,
			requested: 0,
			details: [],
		};

		const userPtos = usersPto.filter((pto) => pto.user._id === element.user._id);
		let buyout = 0;
		userPtos.forEach((pto) => {
			if (pto.payType === "Vacation") {
				record.requested = pto.hours;
			}
			if (pto.payType === "Buyout") {
				buyout = pto.hours;
			}
		});
		record.requested += buyout;
		recordsMap.set(element.user._id, record);
	});
	return Array.from(recordsMap.values());
};

const getPositionRecords = (position: string, records: IDepartmentPosition[] = []) => {
	const results = records.filter((record) => record.position === position);

	results.forEach((result) => delete result.position);
	return results;
};

interface IIngestionState {
	personalTimeOffIngesting: boolean;
	personalTimeOffBalanceIngesting: boolean;
}
interface IPtoProps {
	selected: IPeriodTreeSelection;
	managedPositions: string[] | null | undefined;
	isLoadingPositions: boolean;
	isUpdatable: boolean;
	isAccessGranted: boolean;
}

const PtoBody = (props: IPtoProps) => {
	const { selected, managedPositions, isLoadingPositions, isUpdatable, isAccessGranted } = props;
	const [ingesting, setIngesting] = useState<IIngestionState>({
		personalTimeOffIngesting: false,
		personalTimeOffBalanceIngesting: false,
	});

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();

	// Access
	const { useAuth } = useAuthorization();
	const SYNC_TIME_AND_ATTENDANCE = useAuth(actionsList.SYNC_TIME_AND_ATTENDANCE);

	// Subscriptions
	const { getSubscription } = useSubscriptions();
	const ptoSubscription = getSubscription(["timeAndPayRates", selected?.payPeriodId]);

	ptoSubscription?.subscriptions?.onUpdatePto?.setCallback((result: GraphqlSubscriptionResponse<"onUpdatePto", IPayPeriodPto>) => {
		const updatedPto = result.data?.onUpdatePto;
		if (!updatedPto) {
			return;
		}
		queryClient.setQueryData(["personalTimeOff", locationId, selected?.cycleId, selected?.payPeriodId], () => updatedPto);
		setIngesting((prev) => ({ ...prev, personalTimeOffIngesting: false }));
	});
	ptoSubscription?.subscriptions?.onUpdatePtoBalance?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdatePtoBalance", IPayPeriodPto>) => {
			const updatedPtoBalance = result.data?.onUpdatePtoBalance;
			if (!updatedPtoBalance) {
				return;
			}
			queryClient.setQueryData(
				["personalTimeOffBalance", locationId, selected?.cycleId, selected?.payPeriodId],
				() => updatedPtoBalance
			);
			setIngesting((prev) => ({ ...prev, personalTimeOffBalanceIngesting: false }));
		}
	);

	const queryResults = useQueries({
		queries: [
			{
				queryKey: ["personalTimeOff", locationId, selected?.cycleId, selected?.payPeriodId],
				queryFn: async () => getPto(locationId, selected?.cycleId, selected?.payPeriodId),
				enabled: or(!!managedPositions, !!locationId, !!selected?.payPeriodId),
				staleTime: Infinity,
			},
			{
				queryKey: ["personalTimeOffBalance", locationId, selected?.cycleId, selected?.payPeriodId],
				queryFn: async () => getPtoBalance(locationId, selected?.cycleId, selected?.payPeriodId),
				enabled: or(!!managedPositions, !!locationId, !!selected?.payPeriodId),
				staleTime: Infinity,
			},
		],
	});

	const [ptoQuery, ptoBalanceQuery] = queryResults;

	const { data: pto, isLoading: isLoadingPto } = ptoQuery;
	const { data: ptoBalance, isLoading: isLoadingPtoBalance } = ptoBalanceQuery;

	const { mutate: ingestData } = useMutation({
		onMutate: () => {
			setIngesting({ personalTimeOffIngesting: true, personalTimeOffBalanceIngesting: true });
		},
		mutationFn: async () => {
			const areValid = !!locationId && !!selected?.cycleId && !!selected?.payPeriodId;
			if (!areValid) {
				return false;
			}
			await requestPtoIngestion(locationId, selected?.cycleId, selected?.payPeriodId);
			await requestPtoBalanceIngestion(locationId, selected?.cycleId, selected?.payPeriodId);
			return true;
		},
	});

	const isPartialLoading = or(isLoadingPositions, isLoadingPto, isLoadingPtoBalance);
	const isLoading = or(ingesting.personalTimeOffIngesting, ingesting.personalTimeOffBalanceIngesting, isPartialLoading);
	const isPresent = and(Boolean(pto?.usersPto?.length), Boolean(ptoBalance?.usersPto?.length));

	return (
		<Grid container position={"relative"} alignContent={"flex-start"} sx={{ minHeight: "480px" }}>
			<Grid container item alignContent={"space-between"} sx={{ height: "100%" }}>
				{isAccessGranted && (
					<Grid item xs={12} mb={"50px"}>
						<PtoContent
							isDataPresent={isPresent}
							loading={isLoading}
							data={isPresent ? matchDepartments(managedPositions, ptoBalance?.usersPto, pto?.usersPto) : []}
						/>
					</Grid>
				)}

				{(!isPresent || !isAccessGranted) && !isLoading && (
					<EmptyBody
						image={selected ? emptyTable : empty}
						title={getEmptyTitle(selected, isAccessGranted)}
						bodyHeight={"480px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				)}

				<RenderButton
					{...{
						isPartialLoading,
						isUpdatable,
						pto,
						ptoBalance,
						isAccessGranted,
						SYNC_TIME_AND_ATTENDANCE,
						ingestData,
					}}
				/>
			</Grid>
		</Grid>
	);
};

export default PtoBody;

interface IRenderButtonProps {
	isPartialLoading: boolean;
	isUpdatable: boolean;
	pto: Nullable<IPayPeriodPto>;
	ptoBalance: IPayPeriodPto;
	isAccessGranted: boolean;
	SYNC_TIME_AND_ATTENDANCE: boolean;
	ingestData: () => void;
}

const RenderButton = (props: IRenderButtonProps) => {
	const { isPartialLoading, isUpdatable, ingestData, pto, ptoBalance, isAccessGranted, SYNC_TIME_AND_ATTENDANCE } = props;
	const timezone = useSelector((state: RootState) => state.location.location?.timezone);
	const shouldDisplayContent = or(Boolean(pto?.usersPto?.length), Boolean(ptoBalance?.usersPto?.length));
	return (
		<Grid item xs={12} mb={2.2} className="time-rate-bottomGrid">
			<Stack direction={"row"} alignItems={"center"}>
				<TextIconMessage
					icon
					title={"Last Update"}
					content={getLastUpdatedDate(pto ? pto.updatedAt : ptoBalance?.updatedAt, timezone, DATE_FORMAT.usDateTime12hUnpadded)}
					showContent={shouldDisplayContent}
					loading={isPartialLoading}
					sx={{
						marginRight: isPartialLoading || pto || ptoBalance ? "10px" : 0,
						color: "var(--text-color)",
					}}
				/>

				{isUpdatable && (
					<CountDownButton
						disabled={!SYNC_TIME_AND_ATTENDANCE || !isAccessGranted}
						typographySx={{ fontSize: "12px" }}
						initialCopy={"Update Now"}
						actionCopy={"Updating..."}
						loading={isPartialLoading}
						counter={60}
						onClickCallBack={() => ingestData()}
					/>
				)}
			</Stack>
		</Grid>
	);
};
